import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

/**
 * Which spa client implementation the admin portal uses to connect to a spa.
 *
 * - 'iot'    -> the new @geckoal/gecko-iot-client-js client (supports more devices
 *               and functionality). This is the new default.
 * - 'vessel' -> the legacy @geckoal/vessel-client client.
 */
export type SpaClientType = 'iot' | 'vessel'

/** localStorage key used to persist the admin's spa-client selection per browser. */
export const SPA_CLIENT_STORAGE_KEY = 'gecko-admin.spaClientType'

const VALID_CLIENT_TYPES: SpaClientType[] = ['iot', 'vessel']

function isValidClientType(value: unknown): value is SpaClientType {
    return typeof value === 'string' && VALID_CLIENT_TYPES.includes(value as SpaClientType)
}

/**
 * Resolve the initial client type.
 *
 * Precedence (highest first):
 *   1. A valid value previously persisted to localStorage (the admin's own choice).
 *   2. The VITE_SPA_CLIENT_DEFAULT env var when it equals 'vessel'.
 *   3. 'iot' (the new default).
 */
function resolveInitialClientType(): SpaClientType {
    // localStorage wins over the env default when present and valid.
    try {
        const stored = globalThis.localStorage?.getItem(SPA_CLIENT_STORAGE_KEY)
        if (isValidClientType(stored)) return stored
    } catch {
        // localStorage may be unavailable (private mode, SSR). Fall through to the env default.
    }

    const envDefault = import.meta.env.VITE_SPA_CLIENT_DEFAULT
    if (envDefault === 'vessel') return 'vessel'

    return 'iot'
}

/**
 * Toggle between the new iot client (default) and the legacy vessel client.
 *
 * This setting ONLY affects the admin portal: it controls which client the
 * spa-connection views (GeckoSpaStatus, VesselSchedulesCalendar, SpaStateValidator)
 * use via the useSpaClient composable. It does not change anything in the Gecko app
 * or any backend. The selection persists per-browser in localStorage so an admin can
 * flip to the legacy client for comparison/debugging without affecting other users.
 */
export const useSpaClientStore = defineStore('spaClient', () => {
    const clientType = ref<SpaClientType>(resolveInitialClientType())

    /** True when the active client is the new iot client. */
    const useIotClient = computed(() => clientType.value === 'iot')

    /** Update the active client type and persist the choice for this browser. */
    function setClientType(type: SpaClientType) {
        if (!isValidClientType(type)) return
        clientType.value = type
        try {
            globalThis.localStorage?.setItem(SPA_CLIENT_STORAGE_KEY, type)
        } catch {
            // Ignore persistence failures (e.g. storage disabled); the in-memory value still updates.
        }
    }

    return { clientType, useIotClient, setClientType }
})
